import React, { useMemo } from 'react';
import { v4 as UUID } from 'uuid';
import styles from '../HighlightsModal.module.scss';
import {
  FormControl,
  FormLabel,
  Input,
  Button,
  Icon,
  IconList,
  List,
  ListItem,
  Text,
} from 'components/core';
import { Inline, Stack } from 'components/layout';

type KeywordFormSectionProps = {
  title: string;
  titleColor: 'success' | 'danger';
  label: string;
  keywords: string[];
  onAdd?: () => void;
  onDelete?: () => void;
};

const KeywordFormSection = ({
  title,
  titleColor,
  label,
  keywords,
  onAdd,
  onDelete,
}: KeywordFormSectionProps) => {
  const describedById = useMemo(() => UUID(), []);

  return (
    <Stack spacing={3} className={styles.formGroup} component="fieldset">
      <Text
        className={styles.formLegend}
        variant={titleColor}
        component="legend"
        size="sm"
      >
        {title}
      </Text>

      <Inline alignY="top" spacing={3}>
        <FormControl>
          <FormLabel isVisuallyHidden>{label}</FormLabel>
          <Input size="sm" aria-describedby={describedById} />
          <Text size="xs" variant="weak" id={describedById}>
            Add a word or phrase
          </Text>
        </FormControl>

        <Button
          htmlType="button"
          type="brand"
          variant="outline"
          aria-label={`Add ${title}`}
          size="sm"
          onClick={onAdd}
        >
          Add
        </Button>
      </Inline>

      <List className={styles.highlightList}>
        {keywords.map((word) => (
          <Inline alignX="left" key={word} spacing={3} component={ListItem}>
            <Text size="sm" className={styles.highlight} variant="neutral">
              {word}
            </Text>
            <Button
              iconOnly
              htmlType="button"
              type="neutral"
              variant="ghost"
              size="sm"
              onClick={onDelete}
            >
              <Icon
                aria-label={`Delete ${word} from ${title.toLowerCase()} words`}
                icon={IconList.light.faTrashCan}
              />
            </Button>
          </Inline>
        ))}
      </List>
    </Stack>
  );
};

export default KeywordFormSection;
